/**
 * ContactForm — Weidner Classic
 *
 * Tower 12 contact page body (https://www.tower12.com/contact-us), under
 * the shared HeroSolidBG "CONTACT US" banner:
 *
 *   1. White full-bleed section
 *   2. Left column: gray "HAVE QUESTIONS" display headline, uppercase
 *      lead sentence, shadowed lead-form card
 *   3. Right column: property map (Google Maps embed)
 *
 * Pet policy / fees / the Contact·Visit·Hours strip live in the Header
 * hamburger drawer on Tower 12 — not on this page. The rise centered
 * parallax form card and PropertyInfoPanel info-card row are omitted.
 *
 * Maintenance checklist when re-syncing with the rise parent:
 *   1. Diff against `@vibesites/theme-rise/components/ContactForm.tsx`.
 *   2. Do not reintroduce the centered parallax-hero form card or the
 *      horizontal PropertyInfoPanel info-card row.
 *   3. Keep the white two-column HAVE QUESTIONS + map layout.
 *   4. Run `pnpm run lint:schema` and `pnpm run typecheck`.
 */

import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: { GOOGLE_MAPS_API_KEY?: string } }).env?.GOOGLE_MAPS_API_KEY) ||
  '';

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const formSlot = useBlockSlot('contactform.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  const heading = useContentToken('contactform.heading') || 'Have Questions';
  const propertyName = (usePropertyField('property_name') as string | null) || 'our community';
  const introDefault = `Fill out the form below, drop us a line, or stop by to learn how you can start living in luxury at ${propertyName}.`;
  const intro = useContentToken('contactform.intro') || introDefault;
  const mapHeading = useContentToken('contactform.map_heading') || 'Find Us';

  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;

  const hasCoords = !!(lat && lng && lat.trim() !== '' && lng.trim() !== '');
  let mapSrc = '';
  if (hasCoords && MAPS_API_KEY) {
    mapSrc = `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=15`;
  } else if (hasCoords) {
    mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
  } else if (address && address.trim() !== '') {
    mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&output=embed`;
  }

  const sectionRef = useScrollReveal<HTMLElement>({ threshold: 0.12 });

  return (
    <>
      <section ref={sectionRef} className="cf-questions" aria-label="Contact form">
        <div className="cf-questions__inner">
          <div className="cf-questions__copy">
            <div className="cf-questions__heading-block">
              <EditableToken path="contactform.heading" as="h2" className="cf-questions__heading">
                {heading}
              </EditableToken>
            </div>

            <EditableToken path="contactform.intro" as="p" className="cf-questions__intro">
              {intro}
            </EditableToken>

            <div className="cf-questions__form">
              <EditableToken path="contactform.form_block" as="div">
                {formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <div className="cf-questions__form-empty">
                    <p>No form block selected</p>
                  </div>
                )}
              </EditableToken>
            </div>
          </div>

          <div className="cf-questions__map">
            <EditableToken path="contactform.map_heading" as="h3" className="cf-questions__map-sr">
              {mapHeading}
            </EditableToken>
            {mapSrc ? (
              <iframe
                className="cf-questions__map-frame"
                src={mapSrc}
                title={typeof mapHeading === 'string' ? mapHeading : 'Find Us'}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="cf-questions__map-placeholder">
                <p>Map location not available</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .cf-questions {
          background: #fff;
          padding: 2.75rem 1.25rem 3.25rem;
        }
        @media (min-width: 768px) {
          .cf-questions {
            padding: 3.25rem 2.5rem 4rem;
          }
        }
        @media (min-width: 1200px) {
          .cf-questions {
            padding: 3.5rem 3rem 4.5rem;
          }
        }

        .cf-questions__inner {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          gap: 2.5rem;
          align-items: start;
        }
        @media (min-width: 992px) {
          .cf-questions__inner {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 2.5rem 3rem;
          }
        }

        .cf-questions__heading-block {
          border-left: 10px solid #54595f;
          padding: 0 0 0 1.5rem;
          margin: 0 0 1.25rem;
          text-align: left;
        }

        .cf-questions__heading {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-size: 3rem;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0;
          text-transform: uppercase;
          color: #b6b6b6;
        }
        @media (min-width: 768px) {
          .cf-questions__heading { font-size: 4rem; }
        }
        @media (min-width: 1200px) {
          .cf-questions__heading { font-size: 5rem; }
        }

        .cf-questions__intro {
          margin: 0 0 1.75rem;
          max-width: 36rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 700;
          line-height: 1.65;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #4b4d51;
          text-align: left;
        }

        .cf-questions__form {
          width: 100%;
          max-width: 36rem;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 0 13px -1px rgba(0, 0, 0, 0.49);
          background: #fff;
        }
        .cf-questions__form-empty {
          background: #fff;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted, #6b7280);
        }
        .cf-questions__form-empty p {
          margin: 0;
        }

        .cf-questions__map {
          position: relative;
          width: 100%;
          min-height: 300px;
          overflow: hidden;
          background: #f5f5f5;
        }
        @media (min-width: 992px) {
          .cf-questions__map {
            min-height: 575px;
            align-self: stretch;
          }
        }
        .cf-questions__map-frame {
          display: block;
          width: 100%;
          height: 300px;
          border: 0;
        }
        @media (min-width: 992px) {
          .cf-questions__map-frame {
            position: absolute;
            inset: 0;
            height: 100%;
            min-height: 575px;
          }
        }
        .cf-questions__map-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          height: 100%;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          color: var(--vibe-color-text-muted, #6b7280);
        }
        @media (min-width: 992px) {
          .cf-questions__map-placeholder { min-height: 575px; }
        }
        .cf-questions__map-placeholder p { margin: 0; }

        .cf-questions__map-sr {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }

        /* Scroll reveal */
        .cf-questions .cf-questions__heading-block,
        .cf-questions .cf-questions__intro,
        .cf-questions .cf-questions__form,
        .cf-questions .cf-questions__map {
          transition:
            opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
            transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .cf-questions.reveal--visible .cf-questions__intro {
          transition-delay: 0.1s;
        }
        .cf-questions.reveal--visible .cf-questions__form {
          transition-delay: 0.18s;
        }
        .cf-questions.reveal--visible .cf-questions__map {
          transition-delay: 0.22s;
        }
        .cf-questions.reveal--hidden .cf-questions__heading-block {
          opacity: 0;
          transform: translateX(-1.25rem);
        }
        .cf-questions.reveal--hidden .cf-questions__intro,
        .cf-questions.reveal--hidden .cf-questions__form {
          opacity: 0;
          transform: translateY(1.25rem);
        }
        .cf-questions.reveal--hidden .cf-questions__map {
          opacity: 0;
          transform: translateX(1.25rem);
        }
        .cf-questions.reveal--visible .cf-questions__heading-block,
        .cf-questions.reveal--visible .cf-questions__intro,
        .cf-questions.reveal--visible .cf-questions__form,
        .cf-questions.reveal--visible .cf-questions__map {
          opacity: 1;
          transform: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .cf-questions.reveal--hidden .cf-questions__heading-block,
          .cf-questions.reveal--hidden .cf-questions__intro,
          .cf-questions.reveal--hidden .cf-questions__form,
          .cf-questions.reveal--hidden .cf-questions__map {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Have Questions',
    },
    'contactform.intro': {
      label: 'Intro',
      type: 'text',
      defaultValue:
        'Fill out the form below, drop us a line, or stop by to learn how you can start living in luxury at our community.',
    },
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.map_heading': {
      label: 'Map Heading (screen-reader label)',
      type: 'text',
      defaultValue: 'Find Us',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
